<template>
    <div class="chips-autocomplete" @click="focusInput">
        <div class="chips">
            <span v-for="(chip, i) in modelValue" :key="chip" class="chip">
                {{ displayLabel(chip) }}
                <button type="button" class="chip-x" @click.stop="remove(i)" aria-label="Usuń wykluczenie">
                    <font-awesome-icon icon="xmark" />
                </button>
            </span>

            <input ref="inputEl" v-model="query" class="chips-input" :placeholder="modelValue.length ? '' : placeholder"
                autocomplete="off" role="combobox" aria-autocomplete="list" :aria-expanded="showList ? 'true' : 'false'"
                :aria-controls="listboxId" @focus="focused = true" @blur="onBlur"
                @keydown.enter.prevent="tryAddHighlightedOrRaw" @keydown.tab.prevent="tryAddHighlightedOrRaw"
                @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.backspace="backspaceBehavior" />
        </div>

        <ul v-if="showList" class="suggestions" role="listbox" :id="listboxId">
            <li v-for="(opt, idx) in filtered" :key="opt[valueKey]" role="option"
                :aria-selected="idx === highlighted ? 'true' : 'false'" :class="{ active: idx === highlighted }"
                @mousedown.prevent="add(opt[valueKey])">
                <strong>{{ opt[valueKey] }}</strong>
                <small v-if="labelKey && opt[labelKey]"> — {{ opt[labelKey] }}</small>
            </li>
            <li v-if="!filtered.length" class="nores">Brak wyników</li>
        </ul>
    </div>
</template>

<script>
export default {
    name: 'ChipsAutocomplete',
    props: {
        modelValue: { type: Array, default: () => [] },
        options: { type: Array, default: () => [] },
        valueKey: { type: String, default: 'code' },
        labelKey: { type: String, default: 'label' },
        placeholder: { type: String, default: 'Wpisz, aby wyszukać...' },
        maxSuggestions: { type: Number, default: 8 },
        allowFreeText: { type: Boolean, default: false }
    },
    data() {
        return {
            query: '',
            highlighted: 0,
            focused: false,
            listboxId: `chips-listbox-${Math.random().toString(36).slice(2)}`
        }
    },
    computed: {
        normalizedOptions() {
            return this.options.filter(o => o && o[this.valueKey]);
        },
        filtered() {
            const q = this.query.trim().toLowerCase();
            const list = this.normalizedOptions
                .filter(o =>
                    !this.modelValue.includes(o[this.valueKey]) &&
                    (
                        (o[this.valueKey] || '').toString().toLowerCase().includes(q) ||
                        (this.labelKey && (o[this.labelKey] || '').toLowerCase().includes(q))
                    )
                );
            return list.slice(0, this.maxSuggestions);
        },
        showList() {
            return this.focused && this.query.length > 0;
        }
    },
    methods: {
        focusInput() {
            this.$refs.inputEl?.focus();
            this.focused = true;
        },
        onBlur() {
            setTimeout(() => { this.focused = false }, 100);
        },
        move(dir) {
            if (!this.filtered.length) return;
            const next = this.highlighted + dir;
            this.highlighted = (next + this.filtered.length) % this.filtered.length;
        },
        tryAddHighlightedOrRaw() {
            if (this.filtered[this.highlighted]) {
                this.add(this.filtered[this.highlighted][this.valueKey]);
            } else if (this.allowFreeText && this.query.trim()) {
                this.add(this.query.trim());
            }
        },
        add(value) {
            if (!value || this.modelValue.includes(value)) return;
            this.$emit('update:modelValue', [...this.modelValue, value]);
            this.query = '';
            this.highlighted = 0;
            this.$nextTick(() => this.$refs.inputEl?.focus());
        },
        remove(index) {
            const copy = [...this.modelValue];
            copy.splice(index, 1);
            this.$emit('update:modelValue', copy);
            this.$nextTick(() => this.$refs.inputEl?.focus());
        },
        backspaceBehavior(e) {
            if (!this.query && this.modelValue.length) {
                e.preventDefault();
                this.remove(this.modelValue.length - 1);
            }
        },
        displayLabel(value) {
            const found = this.normalizedOptions.find(o => o[this.valueKey] === value);
            if (!found) return value;
            return this.labelKey && found[this.labelKey]
                ? `${found[this.valueKey]} — ${found[this.labelKey]}`
                : found[this.valueKey];
        }
    }
}
</script>

<style scoped lang="scss">
.chips-autocomplete {
    position: relative;
}

.chips {
    display: flex;
    flex-wrap: wrap;
    gap: .45rem;
    padding: 8px;
    border: 2px solid $color-grey;
    border-radius: 10px;
    background: $color-white;
    transition: border-color .2s ease, box-shadow .2s ease;

    &:focus-within {
        border-color: $color-primary;
        box-shadow: 0 0 0 4px rgba($color-primary, .12);
    }
}

.chip {
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    padding: 6px 10px;
    border-radius: 9999px;
    background: rgba($color-primary, .10);
    border: 1px solid rgba($color-primary, .25);
    color: $color-primary-dark;
    font-size: .9rem;
    line-height: 1;
}

.chip-x {
    border: 0;
    background: transparent;
    cursor: pointer;
    line-height: 1;
    border-radius: 6px;
    padding: 0 4px;
    font-size: 1rem;
    color: inherit;
    transition: background-color .15s ease, opacity .2s ease;

    &:hover {
        background: rgba($color-primary, .12);
    }

    &:focus-visible {
        outline: 2px solid $color-primary;
        outline-offset: 2px;
    }
}

.chips-input {
    flex: 1;
    min-width: 140px;
    border: 0;
    outline: 0;
    font-size: 1rem;
    padding: 6px;
    color: $color-text;
    background: transparent;
}

.suggestions {
    position: absolute;
    z-index: 10;
    left: 0;
    right: 0;
    margin-top: 6px;
    background: $color-white;
    border: 1px solid $color-grey;
    border-radius: 10px;
    max-height: 260px;
    overflow: auto;
    box-shadow: 0 12px 28px rgba(0, 0, 0, .10);
}

.suggestions li {
    padding: 10px 12px;
    cursor: pointer;
    display: flex;
    gap: .5rem;
    align-items: baseline;
    font-size: .95rem;
    color: $color-text;
    transition: background-color .15s ease;

    strong {
        font-weight: 700;
        color: $color-text;
    }

    small {
        color: $color-text-light;
    }
}

.suggestions li.active,
.suggestions li:hover {
    background: rgba($color-primary, .08);
}

.nores {
    color: $color-text-light;
    padding: 10px 12px;
}

@media (max-width: 640px) {
    .chips {
        padding: 10px;
        gap: .5rem;
    }

    .chip {
        padding: 8px 12px;
        font-size: .95rem;
    }

    .chips-input {
        padding: 8px;
        min-width: 120px;
    }
}
</style>